import { isAxiosError } from 'axios';
import { create } from 'zustand';
import { notificationService, StaleCursorError } from '../services/notificationService';
import { louderTone, toneForTransition, type ArrivalTone } from '../lib/arrivalAnnounce';
import {
  foldBatch,
  foldBatchPinned,
  isContiguous,
  upsertInto,
  upsertPinned,
} from '../lib/notificationMerge';
import type {
  GetNotificationsParams,
  Notification,
  NotificationCounts,
  NotificationTab,
} from '../types/notification.types';

/**
 * N5 (86d3w2kg2) — the Notification Center's app-lifetime store.
 *
 * <b>The first app-lifetime store + SSE stream in this codebase</b> (every existing stream is
 * page-scoped), which is why reset semantics are spelled out rather than assumed: a TAB change
 * resets and bumps `reqSeq`; navigating away from a notification surface and back does NOT reset;
 * logout resets everything.
 *
 * ⚠️ REACT COMPILER (86d39bx22 / 86d3d1z40). Every scalar here — the four counts, `hasMore`,
 * `nextCursor`, `isLoading`, `isLoadingMore` — is a PRIMITIVE field read through a direct selector
 * (`useNotificationStore((s) => s.needsCount)`). Never a getter, never a `.filter().length` inside a
 * selector: the compiler memoizes on the stable function reference and serves stale UI, which is how
 * `disabled={!hasMore()}` leaves a button permanently disabled while the data underneath it moves.
 *
 * ⚠️ COUNTS ARE SERVER-AUTHORITATIVE AND ONLY EVER ASSIGNED. There is deliberately no `recount()`
 * anywhere in this file. A recount runs over the loaded window, and a window is a subset while a
 * badge is a total — a user with 200 unread rows and a 50-row window would show a badge of 50. They
 * arrive on four paths: `/counts` at boot, the page envelope, every mutation response, every SSE
 * frame.
 */

const ZERO_COUNTS: NotificationCounts = {
  needsCount: 0,
  unreadCount: 0,
  activeCount: 0,
  doneCount: 0,
};

interface NotificationState {
  items: Notification[];
  pinned: Notification[];
  pinnedTruncated: boolean;

  nextCursor: string | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;

  needsCount: number;
  unreadCount: number;
  activeCount: number;
  doneCount: number;

  serverTimeUtc: string | null;

  /**
   * N7 — how far the browser's clock sits from the server's, in milliseconds
   * (`serverTime - clientTime`, measured the moment a page envelope lands).
   *
   * A PRIMITIVE, so rows can read it through a direct selector. Rows add it to `Date.now()` to get a
   * ticking server-aligned instant, which is what "next retry in 6m" has to be measured against: on a
   * browser running two minutes fast, an unskewed countdown reads "any moment now" while the job is
   * still waiting, and the user presses Retry on a posting that was about to retry itself.
   *
   * ⛔ Not `serverTimeUtc` alone. That is a frozen stamp — a popover left open for ten minutes would
   * show every row's age stuck at the value it had on open.
   */
  serverSkewMs: number;

  tab: NotificationTab;
  error: string | null;

  /**
   * Stale-RESPONSE guard. Bumped on every reset (tab change, gap-truncating resync); an in-flight
   * response whose sequence no longer matches is discarded rather than applied.
   */
  reqSeq: number;

  /**
   * Bumped once per ANNOUNCEABLE TRANSITION. The bell watches it to replay its ripple and play its
   * tone; nothing else reads it.
   *
   * ⚠️ A COUNTER, not a flag — the bell alternates two animation class names off its parity, because
   * re-applying one name to a live element is a no-op and a boolean would ring once and then stay
   * silent forever.
   *
   * ⚠️ It counts TRANSITIONS, not arrivals. See `toneForTransition` for why that distinction is the
   * whole point: our rows are journeys that morph in place, so "an id I have not seen" means
   * "queued" — the one moment the user already knows about.
   */
  arrivalSeq: number;

  /** Which tone the transition behind the current `arrivalSeq` deserves. Null before the first. */
  arrivalTone: ArrivalTone | null;

  fetch: (params?: GetNotificationsParams) => Promise<void>;
  fetchMore: () => Promise<void>;
  setTab: (tab: NotificationTab) => void;

  upsert: (row: Notification, counts?: NotificationCounts, userInitiated?: boolean) => void;
  applyBatch: (rows: Notification[], counts?: NotificationCounts) => void;
  applyCounts: (counts: NotificationCounts) => void;
  hydrateCounts: () => Promise<void>;

  markRead: (ids: number[]) => Promise<void>;
  markUnread: (ids: number[]) => Promise<void>;
  markAllRead: () => Promise<void>;
  markDone: (id: number) => Promise<void>;
  remove: (id: number) => Promise<void>;

  retry: (id: number) => Promise<string | undefined>;
  cancel: (id: number) => Promise<string | undefined>;
  reconcile: (id: number) => Promise<string | undefined>;

  resync: () => Promise<void>;
  reset: () => void;
}

const INITIAL = {
  items: [] as Notification[],
  pinned: [] as Notification[],
  pinnedTruncated: false,
  nextCursor: null as string | null,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  ...ZERO_COUNTS,
  serverTimeUtc: null as string | null,
  serverSkewMs: 0,
  tab: 'all' as NotificationTab,
  error: null as string | null,
  reqSeq: 0,
  arrivalSeq: 0,
  arrivalTone: null as ArrivalTone | null,
};

/**
 * The server's clock, and how far ahead of ours it runs, from one page envelope.
 *
 * Folded into every `set()` that assigns `serverTimeUtc` so the two can never drift apart — a skew
 * measured against one page and a stamp taken from another would be worse than no correction at all.
 * An unparseable stamp yields a skew of 0, i.e. "trust the local clock", which is the only safe
 * fallback: a `NaN` skew would poison every relative time on screen.
 */
/**
 * The version of this row we currently hold, from either list, or undefined.
 *
 * ⚠️ The PREVIOUS ROW, not a boolean — `toneForTransition` compares states, and the whole fix rests
 * on knowing what the row moved FROM. Both lists are searched because `pinned` is a separate server
 * collection: a needs-action row older than the loaded window lives there and nowhere else.
 */
function heldRow(
  items: Notification[],
  pinned: Notification[],
  id: number,
): Notification | undefined {
  return items.find((row) => row.id === id) ?? pinned.find((row) => row.id === id);
}

function serverClockOf(serverTimeUtc: string): { serverTimeUtc: string; serverSkewMs: number } {
  const parsed = Date.parse(serverTimeUtc);
  return {
    serverTimeUtc,
    serverSkewMs: Number.isNaN(parsed) ? 0 : parsed - Date.now(),
  };
}

export const useNotificationStore = create<NotificationState>()((set, get) => ({
  ...INITIAL,

  // ── Reads ──────────────────────────────────────────────────────────────────

  async fetch(params = {}) {
    const seq = get().reqSeq;
    set({ isLoading: true, error: null });

    try {
      const page = await notificationService.getPage({
        tab: get().tab,
        pageSize: 50,
        includeCounts: true,
        ...params,
      });

      // The window was reset under us (a tab click landed mid-flight) — this page describes a
      // question nobody is asking any more.
      if (get().reqSeq !== seq) return;

      set({
        items: page.items,
        pinned: page.pinned ?? [],
        pinnedTruncated: page.pinnedTruncated,
        nextCursor: page.nextCursor,
        hasMore: page.hasMore,
        ...serverClockOf(page.serverTimeUtc),
        ...(page.counts ?? {}),
      });
    } catch (err) {
      if (get().reqSeq !== seq) return;
      set({ error: messageOf(err, 'Could not load notifications') });
    } finally {
      // Cleared unconditionally — including on the stale-discard path above, which returns early.
      if (get().reqSeq === seq) set({ isLoading: false, isLoadingMore: false });
    }
  },

  async fetchMore() {
    const { nextCursor, hasMore, isLoadingMore, reqSeq: seq, tab } = get();
    if (!hasMore || !nextCursor || isLoadingMore) return;

    set({ isLoadingMore: true });

    try {
      const page = await notificationService.getPage({ tab, cursor: nextCursor, pageSize: 50 });

      if (get().reqSeq !== seq) return;

      const items = get().items;
      // The window was replaced underneath the request. Appending would interleave two unrelated
      // slices and leave a hole nothing can reach — so this page is discarded and the window
      // resets to page 1, silently.
      //
      // ⚠️ The reset is LOAD-BEARING, not politeness: a plain `return` here was a silent no-op that
      // left nextCursor/hasMore untouched, and the state is reachable WITHOUT any reqSeq bump —
      // mark a pinned three-week-old row unread and its userInitiated insert lands below the window
      // tail, making the next Show more non-contiguous forever. One click then bricked Show more for
      // the session, no spinner, no error (2026-08-25 review, confirmed 2-0).
      if (!isContiguous(items[items.length - 1], page.items[0])) {
        set({
          items: [],
          nextCursor: null,
          hasMore: false,
          error: null,
          reqSeq: get().reqSeq + 1,
        });
        void get().fetch({ includePinned: true });
        return;
      }

      set({
        items: items.concat(page.items),
        nextCursor: page.nextCursor,
        hasMore: page.hasMore,
        ...(page.counts ?? {}),
      });
    } catch (err) {
      if (get().reqSeq !== seq) return;

      // ⛔ A STALE CURSOR IS NOT AN ERROR THE USER SHOULD SEE. It means the cursor format was
      // versioned up under a client that was mid-scroll — so the bookmark expired, not the feature.
      // Reset to page 1 and refetch in silence: no toast, no banner, scroll position sacrificed. The
      // `reqSeq` bump is load-bearing, not tidiness — it discards any other in-flight page for the
      // old cursor, which would otherwise land on top of the fresh window and reopen the same hole.
      if (err instanceof StaleCursorError) {
        set({
          items: [],
          nextCursor: null,
          hasMore: false,
          error: null,
          reqSeq: get().reqSeq + 1,
        });
        void get().fetch({ includePinned: true });
        return;
      }

      set({ error: messageOf(err, 'Could not load more notifications') });
    } finally {
      // ⚠️ THE `finally` IS THE FIX, not decoration. This used to clear only AFTER the stale guard,
      // and since the guard returns first, one tab click mid-flight bricked "load more" for the whole
      // session — no spinner, no error, recoverable only by reloading the browser.
      set({ isLoadingMore: false });
    }
  },

  setTab(tab) {
    if (get().tab === tab) return;

    // Reset AND bump: the bump is what makes any in-flight page for the old tab discard itself
    // instead of landing in the new tab's window.
    set({
      tab,
      items: [],
      pinned: [],
      pinnedTruncated: false,
      nextCursor: null,
      hasMore: false,
      isLoadingMore: false,
      error: null,
      reqSeq: get().reqSeq + 1,
    });

    void get().fetch({ tab, includePinned: true });
  },

  async hydrateCounts() {
    try {
      set(await notificationService.getCounts());
    } catch {
      // The badge is a convenience; failing to hydrate it must never block the shell from rendering.
    }
  },

  // ── Merge entry points ─────────────────────────────────────────────────────

  upsert(row, counts, userInitiated = false) {
    const { items, pinned, tab, hasMore, arrivalSeq } = get();

    // ⚠️ THIS is the path a single live notification takes — the `upsert` frame is what N2 publishes
    // for BOTH the queued emit and the terminal morph. `applyBatch` is the reconciler's bulk frame.
    // An announce signal wired only to the batch path looks correct in tests and never fires in
    // production, which is exactly what happened on the first cut of this.
    //
    // `userInitiated` is the user's own click coming back from the server (retry, cancel). Never
    // announced: they are looking at the row they just pressed.
    const tone = userInitiated ? null : toneForTransition(heldRow(items, pinned, row.id), row);

    set({
      items: upsertInto(items, row, tab, hasMore, userInitiated),
      pinned: upsertPinned(pinned, row),
      ...(counts ?? {}),
      ...(tone ? { arrivalSeq: arrivalSeq + 1, arrivalTone: tone } : {}),
    });
  },

  applyBatch(rows, counts) {
    if (rows.length === 0) {
      if (counts) set(counts);
      return;
    }

    const { items, pinned, tab, hasMore, arrivalSeq } = get();

    // ONE tone for the whole frame, the loudest of them. A reconciler frame can carry a dozen
    // transitions; a dozen chimes for one server event is a machine-gun, and the row that failed
    // matters more than the nine that succeeded.
    //
    // Measured against BOTH lists, because the tab we are on decides whether a row lands in `items`
    // at all — a needs-action alert arriving while the user reads the Done tab still counts.
    let tone: ArrivalTone | null = null;
    for (const row of rows) {
      tone = louderTone(tone, toneForTransition(heldRow(items, pinned, row.id), row));
    }

    // ONE set() for N rows and ONE counts assignment — the reason the batched frame exists. Looping
    // `upsert` per row would be N array identities and N badge repaints for a single server event.
    set({
      items: foldBatch(items, rows, tab, hasMore),
      pinned: foldBatchPinned(pinned, rows),
      ...(counts ?? {}),
      ...(tone ? { arrivalSeq: arrivalSeq + 1, arrivalTone: tone } : {}),
    });
  },

  applyCounts(counts) {
    // Counts-only frame (the reconciler after a mass repair). Touches the four numbers and NOTHING
    // else — not items, not pinned, not nextCursor, not hasMore.
    set(counts);
  },

  // ── Mutations ──────────────────────────────────────────────────────────────

  /**
   * ⚠️ N7 made these four OPTIMISTIC. They used to await the round trip and only then repaint, which
   * put a NetSuite-adjacent API call between a menu click and the first visible change — the exact
   * shape the responsiveness gate names. Each now applies locally first, sends, assigns the server's
   * counts on success, and restores the snapshot on failure.
   *
   * The counts still arrive from the server and are never derived locally: the optimistic half moves
   * ROWS, the authoritative half moves NUMBERS, and for the moment between them the badge is briefly
   * one behind the list. That is the correct trade — a stale number for ~200ms costs nothing, whereas
   * a locally-recomputed one is wrong for as long as the user's inbox is bigger than their window.
   */
  async markRead(ids) {
    // ⚠️ Drop ids whose window row is ALREADY read — this store is the dedupe gate for every caller.
    // Two call sites can race the optimistic flip (row-title click then toast Open, or a fast
    // double-click): the second call's snapshot would capture the post-optimistic row, and if BOTH
    // requests then failed, the restores settle in send order and the second one RESURRECTS the
    // optimistic 'read' — a row locally read that the server still holds unread, silently dropped
    // from the Unread tab. Ids not found in the window pass through: the server skips already-read
    // rows, so an extra id costs nothing, but a skipped legitimate one would stick a row bold.
    const readInWindow = new Set(
      snapshot(get, ids)
        .filter((r) => !r.unread)
        .map((r) => r.id),
    );
    ids = ids.filter((id) => !readInWindow.has(id));
    if (ids.length === 0) return;

    const before = snapshot(get, ids);
    for (const row of before) get().upsert({ ...row, unread: false, readAt: nowIso() });

    try {
      set(await notificationService.markRead(ids));
    } catch (err) {
      restore(get, before);
      throw err;
    }
  },

  async markUnread(ids) {
    if (ids.length === 0) return;

    const before = snapshot(get, ids);
    // ⚠️ userInitiated. This is the one mutation that INSERTS into a keyset window, and without the
    // bypass the row is dropped as "below the window" while the badge increments — the user clicks
    // and nothing visibly happens.
    for (const row of before) get().upsert({ ...row, unread: true, readAt: null }, undefined, true);

    try {
      set(await notificationService.markUnread(ids));
    } catch (err) {
      restore(get, before);
      throw err;
    }
  },

  async markAllRead() {
    // ⛔ No optimistic patch, deliberately. This is the one mutation touching rows the client has
    // never loaded, so any local patch is incomplete by construction. Call, assign the server's
    // counts, then refetch page 1 — which is also what proves needs-action rows stayed flagged.
    set(await notificationService.markAllRead());
    await get().fetch({ includePinned: true });
  },

  async markDone(id) {
    const before = snapshot(get, [id]);
    // `done: true` fails `matchesTab` for every tab except `done`, so `upsertInto` REMOVES it — the
    // row archives out of the list on the click rather than on the response.
    for (const row of before) get().upsert({ ...row, done: true });

    try {
      set(await notificationService.markDone(id));
    } catch (err) {
      // ⚠️ A 404 means the row is ALREADY GONE (deleted in another tab; a resync merge cannot see
      // deletions, so ghosts happen). Rolling back would resurrect a row the server no longer has —
      // undeletable, unmarkable, wrong forever. Keep the optimistic archive and move on.
      if (isGone(err)) {
        void get().hydrateCounts();
        return;
      }
      restore(get, before);
      throw err;
    }
  },

  async remove(id) {
    const before = snapshot(get, [id]);
    set({
      items: get().items.filter((n) => n.id !== id),
      pinned: get().pinned.filter((n) => n.id !== id),
    });

    try {
      set(await notificationService.remove(id));
    } catch (err) {
      // Same ghost rule as markDone: a 404 delete already succeeded from the server's point of view.
      if (isGone(err)) {
        void get().hydrateCounts();
        return;
      }
      restore(get, before);
      throw err;
    }
  },

  // ── Recovery ───────────────────────────────────────────────────────────────

  retry: (id) => runRecovery(get, id, 'retry'),
  cancel: (id) => runRecovery(get, id, 'cancel'),
  /**
   * The N4 recovery verb for one ambiguous/reached row.
   *
   * ⛔ NOT `resync()`. That is the read-only whole-feed merge on reconnect and takes no arguments;
   * there is deliberately no no-arg `reconcile()` overload anywhere in this store.
   */
  reconcile: (id) => runRecovery(get, id, 'reconcile'),

  // ── Reconnect ──────────────────────────────────────────────────────────────

  /**
   * Silent whole-feed merge after the stream drops. Deliberately NOT the reset-to-page-1 effect the
   * quotation and activity lists use — that yanks scroll position, and this store is app-lifetime, so
   * the popover may be open and being read when the reconnect fires.
   */
  async resync() {
    const seq = get().reqSeq;

    try {
      const page = await notificationService.getPage({
        tab: get().tab,
        pageSize: 50,
        includeCounts: true,
        includePinned: true,
      });

      if (get().reqSeq !== seq) return;

      const { items, tab, hasMore } = get();
      const head = items[0];

      // A gap: the newest row we hold is no longer on the server's first page, so rows exist between
      // the two that this merge cannot see. Truncating is the only honest fix — and the bump is what
      // stops an in-flight fetchMore from appending rows ~200 positions below into the hole.
      if (head && page.hasMore && !page.items.some((n) => n.id === head.id)) {
        set({
          items: page.items,
          pinned: page.pinned ?? [],
          pinnedTruncated: page.pinnedTruncated,
          nextCursor: page.nextCursor,
          hasMore: page.hasMore,
          ...serverClockOf(page.serverTimeUtc),
          reqSeq: seq + 1,
          ...(page.counts ?? {}),
        });
        return;
      }

      set({
        items: foldBatch(items, page.items, tab, hasMore),
        pinned: page.pinned ?? get().pinned,
        pinnedTruncated: page.pinnedTruncated,
        ...serverClockOf(page.serverTimeUtc),
        ...(page.counts ?? {}),
      });
    } catch {
      // Silent by contract — a reconnect merge must never toast. The next frame or fetch corrects it.
    }
  },

  reset() {
    set({ ...INITIAL });
  },
}));

/**
 * The shared recovery path for retry / cancel / reconcile.
 *
 * ⚠️ A 400 refusal is DATA. The server has just told us the row we clicked was stale and handed back
 * the corrected one, so it is spliced in and the message surfaced — ⛔ never rolled back. Rolling
 * back would restore the lie and re-render the same wrong button the user just pressed.
 *
 * Only a network failure or a non-400 error rolls back, which is why the optimistic snapshot is taken
 * before the call and re-applied in that branch alone.
 */
async function runRecovery(
  get: () => NotificationState,
  id: number,
  verb: 'retry' | 'cancel' | 'reconcile',
): Promise<string | undefined> {
  const before = get().items.find((n) => n.id === id) ?? get().pinned.find((n) => n.id === id);

  // ⚠️ ALL THREE upserts pass `userInitiated`, and every one of them needs it. The window gate drops
  // a row that sorts below the loaded tail as "a page nobody asked for" — correct for a background
  // SSE frame, WRONG for a row the user is looking at and just clicked.
  //
  // Needs-action rows are the ones that get hurt: `pinned` is maintained independently of the window
  // precisely so a three-week-old failure stays visible, so the row being acted on is routinely
  // outside `items`. Without the bypass, discharging one removed it from `pinned` (it stops being
  // pinned) while the gate refused it from `items` — the row belonged to NEITHER collection and
  // vanished mid-interaction. Worse, N4's whole 400-with-body design died with it: `runRecovery`'s
  // refusal `message` is rendered BY that row, so the explanation was discarded before it could be
  // read. Found by adversarial review of the flat-list change, 2026-08-25; the bug predates it.

  // Instant acknowledgement: the row goes in-flight before the request leaves, so the click has
  // visible feedback well inside 100ms rather than waiting on a NetSuite round trip.
  if (before) get().upsert({ ...before, state: 'queued', actions: [] }, undefined, true);

  try {
    const response = await notificationService[verb](id);
    get().upsert(response.result.notification, response.result.counts, true);
    return response.ok ? undefined : response.message;
  } catch (err) {
    if (before) get().upsert(before, undefined, true);
    throw err;
  }
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** The row is already gone server-side — treat the mutation as done, never roll back a ghost. */
function isGone(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 404;
}

// ── Optimistic mutation support (N7) ─────────────────────────────────────────

/**
 * The rows an optimistic mutation is about to touch, as they stand right now.
 *
 * Reads BOTH collections and de-duplicates, because a needs-action row lives in `items` and `pinned`
 * simultaneously and a rollback that restored only one copy would leave the two disagreeing — the
 * pinned section showing a row the list below it had already archived.
 */
function snapshot(get: () => NotificationState, ids: number[]): Notification[] {
  const { items, pinned } = get();
  const byId = new Map<number, Notification>();

  for (const row of items) if (ids.includes(row.id)) byId.set(row.id, row);
  for (const row of pinned) if (ids.includes(row.id)) byId.set(row.id, row);

  return [...byId.values()];
}

/**
 * Puts a snapshot back after a failed mutation.
 *
 * ⚠️ `userInitiated` is MANDATORY here. Rollback runs through the same window gate as any upsert, and
 * a row restored after a failed Remove may now sort below the loaded window — without the bypass it
 * would be judged "a page nobody asked for" and silently dropped, so a failed delete would look
 * exactly like a successful one.
 */
function restore(get: () => NotificationState, rows: Notification[]): void {
  for (const row of rows) get().upsert(row, undefined, true);
}

/**
 * A local `readAt` for the optimistic half of a read/unread toggle. Display-only and overwritten by
 * the server's value on the next frame or fetch — ⛔ never an ordering input, which is `createdAtMicros`.
 */
function nowIso(): string {
  return new Date().toISOString();
}
